import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Button, Input, Spinner } from "reactstrap";

import { AUTOPILOT_COLORS, autopilotInsetCardStyle } from "../../../components/autopilot/styles";
import { useToast } from "../../../hooks/toast";
import { mensagemErroParametros } from "../../../services/parametrosApi";
import {
  getCamposConectorErp,
  getHistoricoConectorErp,
  salvarConectorErp,
  trocarVersaoConectorErp,
  cancelarAgendamentoConectorErp,
  desativarConectorErp,
  testarConectorErp,
  obterUrlAutorizacaoBling,
  obterUrlAutorizacaoConectorErp,
} from "../../../services/conectorEmbarcadorApi";
import env from "../../../environment";
import { renderControl } from "../Parametros/renderControl";
import { paramParaControle } from "../Parametros/porAssunto/paramParaControle";
import { calcularValoresParaSalvar, valoresAtuaisDoFormulario, valoresPorNome, chaveDe } from "../Parametros/porAssunto/calcularValoresParaSalvar";
import { StatusPill } from "../Transportadoras/capacidadeBadges";
import ConectorLogo from "./ConectorLogo";
import {
  drawerShellStyle,
  drawerHeadStyle,
  drawerEyebrowStyle,
  drawerTitleStyle,
  drawerSubtitleStyle,
  drawerCloseButtonStyle,
  drawerBodyStyle,
} from "../Transportadoras/detalheDrawer.styles";
import { StatusConectorBadge } from "./ConectorCard";
import { rotuloCapacidade } from "./classificacao";
import { statusConectorErp, formatarData, SITUACAO, rotuloDaVersao } from "./statusConectorErp";
import "../Parametros/Parametros.css";

// Painel lateral dos conectores de ERP do catálogo (H4, PO1-3617). Separado do
// ConectorDrawer dos 6 cards fixos, que não muda. Lê os campos da versão
// escolhida (GET /campos?versao=) e o histórico; grava pelo PUT (vincular/salvar),
// agenda ou faz a troca de versão (POST /trocar-versao), cancela a troca,
// desativa e testa a conexão (resultado inline). Feedback por toast, como o
// ConectorDrawer.
const secaoTituloStyle = {
  color: AUTOPILOT_COLORS.muted,
  fontSize: "0.7rem",
  fontWeight: 700,
  letterSpacing: "0.08em",
  textTransform: "uppercase",
  margin: "1rem 0 0.5rem",
};

const labelStyle = { color: AUTOPILOT_COLORS.title, fontSize: "0.84rem", fontWeight: 600, marginBottom: "0.3rem" };
const ajudaStyle = { color: AUTOPILOT_COLORS.muted, fontSize: "0.75rem", lineHeight: 1.5, marginTop: "0.3rem" };
const avisoStyle = {
  border: `1px solid ${AUTOPILOT_COLORS.statusWarningBorder}`,
  background: AUTOPILOT_COLORS.statusWarningBg,
  color: AUTOPILOT_COLORS.text,
  borderRadius: 10,
  padding: "0.55rem 0.75rem",
  fontSize: "0.78rem",
  lineHeight: 1.5,
  marginBottom: "0.5rem",
};
const linhaHistoricoStyle = {
  ...autopilotInsetCardStyle,
  padding: "0.5rem 0.75rem",
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  gap: "0.5rem",
  marginBottom: "0.4rem",
};

function resultadoStyle(ok) {
  return {
    ...autopilotInsetCardStyle,
    border: `1px solid ${ok ? AUTOPILOT_COLORS.statusSuccessBorder : AUTOPILOT_COLORS.statusDangerBorder}`,
    background: ok ? AUTOPILOT_COLORS.statusSuccessBg : AUTOPILOT_COLORS.statusDangerBg,
    color: AUTOPILOT_COLORS.text,
    padding: "0.6rem 0.75rem",
    fontSize: "0.8rem",
    lineHeight: 1.5,
    marginTop: "0.75rem",
  };
}

// PO1-3848 (Bling): parâmetros gravados pelo sistema no fluxo OAuth (tokens e o
// serviço da logística). Ficam na versão (hasValue, troca de versão copia), mas
// não são campos de formulário: o painel mostra "Conectado" + o botão no lugar.
export const BLING_KEY = "bling";
export const CAMPOS_SISTEMA_BLING = ["accessToken", "refreshToken", "tokenExpiraEm", "idServicoEverlog"];
// PO1-3853 (conectores de ERP com OAuth; 1º uso: Tiny / Olist ERP). Por
// conectorKey: os parâmetros gravados pelo sistema no fluxo OAuth (tokens e
// vencimentos) ficam na versão (hasValue, troca de versão copia), mas não são
// campos de formulário; o painel mostra o bloco "Conexão com o <ERP>" com a URL
// de redirecionamento (o cliente cadastra no aplicativo dele), o estado e o
// botão Conectar/Reconectar. `camposCredencial` são os campos que o cliente
// digita e precisa SALVAR antes de conectar (aplicativo privado do cliente).
export const OAUTH_CONECTORES = {
  tiny: {
    nome: "Tiny",
    camposSistema: ["accessToken", "refreshToken", "tokenExpiraEm", "refreshExpiraEm"],
    camposCredencial: ["clientId", "clientSecret"],
    ajuda:
      "Crie um aplicativo na sua conta Tiny (Configurações > Aplicativos / API), cadastre nele a URL de redirecionamento abaixo e cole aqui o Client ID e o Client Secret. Depois de salvar, clique em Conectar ao Tiny para autorizar o acesso.",
    renovacao:
      "A conexão se renova sozinha a cada 4 horas. Se o Everlog ficar mais de 1 dia sem renovar, reconecte a conta.",
  },
};

// URL pública de retorno do backend_hub que o cliente cadastra no aplicativo.
export function urlRetornoOAuth(conectorKey) {
  return `${String(env.API_URL || "").replace(/\/+$/, "")}/public/conectores-erp/${conectorKey}/retorno`;
}

const PILL_OAUTH = {
  conectado: { status: "ativo", rotulo: "Conectado" },
  expirado: { status: "erro_credencial", rotulo: "Conexão expirada, reconecte" },
  nao_conectado: { status: "pendente", rotulo: "Não conectado" },
};

const PILL_HISTORICO = {
  vigente: { status: "ativo", rotulo: "Vigente" },
  agendado: { status: "pendente", rotulo: "Agendado" },
  encerrado: { status: "sem-conector", rotulo: "Encerrado" },
  // Status=0 só acontece numa troca agendada que foi cancelada (nunca valeu).
  desativado: { status: "erro_credencial", rotulo: "Cancelada" },
};

// Data de hoje no fuso do navegador (toISOString é UTC e vira o dia depois das 21h).
export const hojeIso = (agora = new Date()) => {
  const p = (n) => String(n).padStart(2, "0");
  return `${agora.getFullYear()}-${p(agora.getMonth() + 1)}-${p(agora.getDate())}`;
};

// Rótulo do campo: capacidade pelo nome da tela ("NF-e"); o resto pela
// descrição do parâmetro ("Usuário de acesso ao ERP"), não pelo nome técnico.
// PO1-3859 (épico PO1-3858): texto de ajuda do endereço do webhook, por conector. O endereço
// é sempre exclusivo do vínculo; o que muda é onde o cliente cola e quais eventos liga.
const AJUDA_WEBHOOK = {
  omie: "No ERP (Omie: Developer > Aplicativos > Adicionar novo webhook), cole este endereço e ative os eventos de pedido de venda e de NF-e.",
  comprovei:
    "No Comprovei, peça ao suporte deles a ativação do módulo de webhooks apontando para este endereço (eventos de documento: entrega realizada, não realizada, chegada, devolução e agendamento). Sem o módulo, o Everlog consulta o status das NF-e por chave de hora em hora.",
  simpliroute:
    "Na SimpliRoute (Add-ons > Webhooks, ou pela API /v1/addons/webhooks/), cadastre este endereço nos eventos visit_checkout_detailed, visit_checkout e on_its_way. A visita precisa levar a chave da NF-e (44 dígitos) no campo reference para o Everlog achar a nota.",
};
const AJUDA_WEBHOOK_PADRAO = "No sistema de origem, cole este endereço no cadastro de webhooks e ative os eventos que o Everlog deve receber.";

export function ajudaWebhook(conectorKey) {
  return `${AJUDA_WEBHOOK[conectorKey] || AJUDA_WEBHOOK_PADRAO} O endereço é exclusivo deste vínculo.`;
}

export function rotuloCampo(p) {
  if (!p) return "";
  if (p.ehCapacidade) return rotuloCapacidade(String(p.nome).replace(/^cap_/, ""));
  return p.descricao || p.nome;
}

export default function ConectorErpDrawer({ conector, embarcadorId, onClose, onSalvo }) {
  const { addToast } = useToast();
  const [dados, setDados] = useState(null); // resposta de GET /campos
  const [historico, setHistorico] = useState([]);
  const [versaoSel, setVersaoSel] = useState(null);
  const [dataInicio, setDataInicio] = useState("");
  const [alterados, setAlterados] = useState({});
  const [loading, setLoading] = useState(false);
  const [erro, setErro] = useState("");
  const [salvando, setSalvando] = useState(false);
  const [testando, setTestando] = useState(false);
  const [resultadoTeste, setResultadoTeste] = useState(null);
  const [confirmando, setConfirmando] = useState(null); // 'desativar' | 'cancelar' | null
  const [conectando, setConectando] = useState(false); // PO1-3848/PO1-3853: indo para o provedor OAuth
  const [webhookCopiado, setWebhookCopiado] = useState(false); // PO1-3844: botão Copiar do endereço do webhook
  const [copiado, setCopiado] = useState(false); // PO1-3853: URL de redirecionamento copiada

  const notify = (type, title, description) => addToast({ place: "tr", type, title, description });

  // PO1-3844 (Omie): copia o endereço público do webhook (GET /campos ->
  // webhookUrl). Clipboard bloqueada: o operador seleciona o texto e copia à mão.
  const copiarWebhook = useCallback(async () => {
    const url = dados && dados.webhookUrl;
    if (!url) return;
    try {
      if (navigator.clipboard) {
        await navigator.clipboard.writeText(url);
        setWebhookCopiado(true);
        setTimeout(() => setWebhookCopiado(false), 2000);
      }
    } catch (_) {
      // Clipboard bloqueada: o operador copia à mão.
    }
  }, [dados]);

  const carregar = useCallback(
    async (versao) => {
      if (!embarcadorId || !conector) return;
      setLoading(true);
      setErro("");
      try {
        const [c, h] = await Promise.all([
          getCamposConectorErp(embarcadorId, conector.conectorKey, versao || undefined),
          getHistoricoConectorErp(embarcadorId, conector.conectorKey).catch(() => []),
        ]);
        setDados(c);
        setHistorico(h);
        setVersaoSel(c && c.versao ? String(c.versao) : null);
      } catch (e) {
        setErro(mensagemErroParametros(e, "Falha ao carregar o conector."));
      } finally {
        setLoading(false);
      }
    },
    [embarcadorId, conector],
  );

  // Troca de card: recarrega do zero.
  useEffect(() => {
    setAlterados({});
    setResultadoTeste(null);
    setConfirmando(null);
    setDataInicio("");
    setDados(null);
    carregar(null);
  }, [carregar]);

  const campos = useMemo(() => (dados && Array.isArray(dados.campos) ? dados.campos : []), [dados]);
  const ehBling = conector.conectorKey === BLING_KEY;
  const oauth = OAUTH_CONECTORES[conector.conectorKey] || null;
  // PO1-3848 (Bling) e PO1-3853 (OAuth por conectorKey, Tiny): os tokens não são
  // digitados; a conta é conectada pelo botão. Só a credencial do aplicativo
  // (quando houver) e os demais campos ficam.
  const camposConfig = useMemo(
    () =>
      campos.filter(
        (c) =>
          !c.ehCapacidade &&
          !(ehBling && CAMPOS_SISTEMA_BLING.indexOf(c.nome) !== -1) &&
          !(oauth && oauth.camposSistema.indexOf(c.nome) !== -1),
      ),
    [campos, ehBling, oauth],
  );
  const blingConectado = useMemo(() => ehBling && campos.some((c) => c.nome === "refreshToken" && c.hasValue), [campos, ehBling]);
  const temValor = (nome) => campos.some((c) => c.nome === nome && c.hasValue);
  const estadoOAuth = useMemo(() => {
    if (!oauth) return null;
    if (campos.some((c) => c.nome === "refreshToken" && c.hasValue)) return "conectado";
    if (campos.some((c) => c.nome === "tokenExpiraEm" && c.hasValue)) return "expirado";
    return "nao_conectado";
  }, [campos, oauth]);
  const credencialSalva = useMemo(() => Boolean(oauth) && oauth.camposCredencial.every((nome) => temValor(nome)), [campos, oauth]); // eslint-disable-line react-hooks/exhaustive-deps
  const camposCap = useMemo(() => campos.filter((c) => c.ehCapacidade), [campos]);
  const valoresAtuais = useMemo(() => valoresAtuaisDoFormulario(campos, alterados), [campos, alterados]);
  const diff = useMemo(() => calcularValoresParaSalvar(campos, alterados), [campos, alterados]);

  const situacao = (dados && dados.situacao) || conector.situacao || { status: SITUACAO.NAO_CONFIGURADO, vigente: null, agendado: null };
  const vigente = situacao.vigente || null;
  const agendado = situacao.agendado || null;
  const versoes = conector.versoes || [];
  const { status, rotulo } = statusConectorErp(situacao);

  // O que o botão principal faz com a versão escolhida:
  //   configurar: sem vínculo → PUT cria (data opcional = início da vigência);
  //   salvar: versão do vínculo aberto (vigente ou agendado) → PUT grava valores;
  //   trocar: outra versão com vigente → POST trocar-versao (data obrigatória);
  //   bloqueado: outra versão com troca já agendada.
  let modo = "configurar";
  if (vigente || agendado) {
    const abertoNaVersao = [vigente, agendado].some((v) => v && String(v.versao) === String(versaoSel));
    if (abertoNaVersao) modo = "salvar";
    else if (agendado) modo = "bloqueado";
    else modo = "trocar";
  }

  // Ao vincular ou trocar de versão, as capacidades que a versão suporta e que
  // ainda não têm valor já vêm ligadas (o card anuncia "Troca CT-e, NF-e...");
  // as que vieram da vigência anterior mantêm a escolha. O usuário desliga o
  // que não quer.
  const ligarCapacidades = modo === "configurar" || modo === "trocar";
  useEffect(() => {
    if (!ligarCapacidades || !campos.length) return;
    const padrao = {};
    campos.forEach((p) => {
      if (p.ehCapacidade && !p.hasValue && !p.valor) padrao[chaveDe(p)] = "Sim";
    });
    if (Object.keys(padrao).length) setAlterados((prev) => ({ ...padrao, ...prev }));
  }, [campos, ligarCapacidades]);

  const ocupado = salvando || testando || loading || conectando;
  const setValor = (chave, valor) => setAlterados((prev) => ({ ...prev, [chave]: valor }));

  // PO1-3848: pede ao backend a URL de autorização (state assinado) e leva a
  // página inteira ao Bling; o retorno cai em /app/integracoes?conector=bling.
  const conectarBling = async () => {
    setConectando(true);
    try {
      const url = await obterUrlAutorizacaoBling(embarcadorId);
      if (!url) throw new Error("URL de autorização vazia.");
      window.location.assign(url);
    } catch (e) {
      notify("danger", "Conectar ao Bling", mensagemErroParametros(e, "Não foi possível iniciar a conexão com o Bling."));
      setConectando(false);
    }
  };

  // PO1-3853: pede ao backend a URL de autorização (state assinado) e leva a
  // página inteira ao provedor; o retorno cai em /app/integracoes?conector=<key>.
  const conectarOAuth = async () => {
    if (!oauth) return;
    setConectando(true);
    try {
      const { url } = await obterUrlAutorizacaoConectorErp(embarcadorId, conector.conectorKey);
      if (!url) throw new Error("URL de autorização vazia.");
      window.location.assign(url);
    } catch (e) {
      notify("danger", `Conectar ao ${oauth.nome}`, mensagemErroParametros(e, `Não foi possível iniciar a conexão com o ${oauth.nome}.`));
      setConectando(false);
    }
  };

  const urlRetorno = oauth ? urlRetornoOAuth(conector.conectorKey) : "";
  const copiarUrlRetorno = async () => {
    let ok = false;
    try {
      if (typeof navigator !== "undefined" && navigator.clipboard && navigator.clipboard.writeText) {
        await navigator.clipboard.writeText(urlRetorno);
        ok = true;
      }
    } catch (e) {
      ok = false;
    }
    if (!ok) {
      // Sem clipboard (http ou navegador antigo): seleciona o campo para o Ctrl+C.
      const campo = document.querySelector("[data-testid='oauth-url-retorno']");
      if (campo && typeof campo.select === "function") {
        campo.focus();
        campo.select();
        try {
          ok = Boolean(document.execCommand && document.execCommand("copy"));
        } catch (e) {
          ok = false;
        }
      }
    }
    setCopiado(true);
    setTimeout(() => setCopiado(false), 2000);
    if (!ok) notify("info", "URL de redirecionamento", "Selecione a URL e copie com Ctrl+C.");
  };

  const trocarVersaoSel = (v) => {
    setVersaoSel(v);
    setAlterados({});
    setResultadoTeste(null);
    carregar(v);
  };

  const depoisDeGravar = async () => {
    setAlterados({});
    setConfirmando(null);
    setDataInicio("");
    await carregar(versaoSel);
    if (onSalvo) onSalvo();
  };

  const salvar = async () => {
    if (modo === "salvar" && !diff.length) {
      notify("info", "Nada alterado", "Nenhum campo do conector foi alterado.");
      return;
    }
    setSalvando(true);
    try {
      if (modo === "trocar") {
        // Em branco = hoje = troca imediata (o backend usa GETDATE() para hoje).
        const data = dataInicio || hojeIso();
        const r = await trocarVersaoConectorErp(embarcadorId, conector.conectorKey, { versao: versaoSel, vigenciaInicio: data, valores: diff });
        notify("success", r && r.imediata ? "Versão trocada" : "Troca agendada", r && r.imediata ? `${conector.nome} já está na v${versaoSel}.` : `${conector.nome} passa para a v${versaoSel} em ${formatarData(data)}.`);
      } else {
        const r = await salvarConectorErp(embarcadorId, conector.conectorKey, { versao: versaoSel, vigenciaInicio: modo === "configurar" ? dataInicio : undefined, valores: diff });
        const criado = r && r.criado;
        notify("success", criado ? "Integração ativada" : "Integração salva", `${conector.nome} v${versaoSel}: ${diff.length} campo${diff.length === 1 ? "" : "s"} gravado${diff.length === 1 ? "" : "s"}.`);
      }
      await depoisDeGravar();
    } catch (e) {
      notify("danger", "Erro ao salvar", mensagemErroParametros(e, "Não foi possível salvar a integração."));
    } finally {
      setSalvando(false);
    }
  };

  const cancelarAgendamento = async () => {
    setSalvando(true);
    try {
      await cancelarAgendamentoConectorErp(embarcadorId, conector.conectorKey);
      notify("success", "Troca cancelada", vigente ? `${conector.nome} continua na v${vigente.versao}.` : `${conector.nome} voltou a não configurado.`);
      await depoisDeGravar();
    } catch (e) {
      notify("danger", "Erro ao cancelar", mensagemErroParametros(e, "Não foi possível cancelar a troca."));
    } finally {
      setSalvando(false);
    }
  };

  const desativar = async () => {
    setSalvando(true);
    try {
      await desativarConectorErp(embarcadorId, conector.conectorKey);
      notify("success", "Integração desativada", `${conector.nome} não será mais consultado.`);
      await depoisDeGravar();
    } catch (e) {
      notify("danger", "Erro ao desativar", mensagemErroParametros(e, "Não foi possível desativar a integração."));
    } finally {
      setSalvando(false);
    }
  };

  // Testa com o que está NA TELA (salvo + alterado), por nome. Senha vazia não
  // vai: o hub-backend usa a salva.
  const testar = async () => {
    const valores = valoresPorNome(campos, valoresAtuais, { omitirSenhaVazia: true });
    setTestando(true);
    setResultadoTeste(null);
    try {
      const r = await testarConectorErp(embarcadorId, conector.conectorKey, versaoSel, valores);
      setResultadoTeste({
        ok: !!(r && r.ok),
        mensagem: (r && r.mensagem) || (r && r.ok ? "Conexão validada." : "Não foi possível validar a conexão."),
        duracaoMs: r && typeof r.duracaoMs === "number" ? r.duracaoMs : null,
      });
    } catch (e) {
      setResultadoTeste({ ok: false, mensagem: mensagemErroParametros(e, "Falha ao testar a conexão."), duracaoMs: null });
    } finally {
      setTestando(false);
    }
  };

  const rotuloBotao = () => {
    if (salvando && !confirmando) return <Spinner size="sm" />;
    if (modo === "configurar") return dataInicio && dataInicio > hojeIso() ? "Agendar ativação" : "Ativar integração";
    if (modo === "trocar") return dataInicio && dataInicio > hojeIso() ? "Agendar troca" : "Trocar agora";
    return "Salvar";
  };

  const renderCampo = (p, ajuda) => {
    const chave = chaveDe(p);
    const dirty = Object.prototype.hasOwnProperty.call(alterados, chave);
    return (
      <div key={chave} style={{ marginBottom: "0.9rem", paddingLeft: "0.6rem", borderLeft: `3px solid ${dirty ? AUTOPILOT_COLORS.accent : "transparent"}` }}>
        <div style={labelStyle}>{rotuloCampo(p)}</div>
        {renderControl({
          param: paramParaControle(p),
          valor: valoresAtuais[chave],
          onChange: (v) => setValor(chave, v),
          saving: ocupado,
          fullWidth: true,
        })}
        {ajuda ? <div style={ajudaStyle}>{ajuda}</div> : null}
      </div>
    );
  };

  return (
    <div style={drawerShellStyle} className="conector-drawer">
      <div style={drawerHeadStyle}>
        <div style={{ minWidth: 0, display: "flex", alignItems: "flex-start", gap: "0.6rem" }}>
          <ConectorLogo conector={conector} height={40} />
          <div style={{ minWidth: 0 }}>
            <div style={drawerEyebrowStyle}>
              {conector.categoria} · {conector.direcao}
            </div>
            <div style={drawerTitleStyle}>{conector.nome}</div>
            <div style={{ ...drawerSubtitleStyle, display: "flex", alignItems: "center", gap: "0.4rem", marginTop: "0.3rem" }}>
              <StatusConectorBadge status={status} rotulo={rotulo} />
            </div>
          </div>
        </div>
        <button type="button" onClick={onClose} style={drawerCloseButtonStyle} aria-label="Fechar detalhe">
          ×
        </button>
      </div>

      <div style={drawerBodyStyle}>
        <p style={{ color: AUTOPILOT_COLORS.text, fontSize: "0.84rem", lineHeight: 1.6, margin: 0 }}>{conector.descricaoCurta}</p>

        {erro ? (
          <div style={{ ...avisoStyle, borderColor: AUTOPILOT_COLORS.statusDangerBorder, background: AUTOPILOT_COLORS.statusDangerBg, marginTop: "0.75rem" }}>
            {erro}{" "}
            <Button size="sm" color="info" outline onClick={() => carregar(versaoSel)}>
              Tentar novamente
            </Button>
          </div>
        ) : null}

        {agendado ? (
          <div style={{ ...avisoStyle, marginTop: "0.75rem" }}>
            Troca para a <strong>v{agendado.versao}</strong> agendada para <strong>{formatarData(agendado.vigenciaInicio)}</strong>
            {vigente ? ` (hoje na v${vigente.versao}).` : "."}{" "}
            {confirmando === "cancelar" ? (
              <span style={{ display: "inline-flex", gap: "0.4rem", marginTop: "0.4rem" }}>
                <Button color="danger" size="sm" disabled={salvando} onClick={cancelarAgendamento}>
                  {salvando ? "Cancelando..." : "Confirmar cancelamento"}
                </Button>
                <Button color="secondary" size="sm" outline disabled={salvando} onClick={() => setConfirmando(null)}>
                  Voltar
                </Button>
              </span>
            ) : (
              <Button color="link" size="sm" style={{ padding: 0, fontSize: "0.78rem" }} disabled={ocupado} onClick={() => setConfirmando("cancelar")}>
                Cancelar troca agendada
              </Button>
            )}
          </div>
        ) : null}

        <div style={secaoTituloStyle}>Versão</div>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0.6rem" }}>
          <div>
            <div style={labelStyle}>Versão do conector</div>
            <Input type="select" bsSize="sm" value={versaoSel || ""} disabled={ocupado || !versoes.length} onChange={(e) => trocarVersaoSel(e.target.value)}>
              {versoes.map((v) => (
                <option key={v.versao} value={v.versao}>
                  v{v.versao}
                  {rotuloDaVersao(v, conector.nome) ? ` · ${rotuloDaVersao(v, conector.nome)}` : ""}
                  {vigente && String(vigente.versao) === String(v.versao) ? " (vigente)" : ""}
                  {agendado && String(agendado.versao) === String(v.versao) ? " (agendada)" : ""}
                </option>
              ))}
            </Input>
          </div>
          {modo === "configurar" || modo === "trocar" ? (
            <div>
              <div style={labelStyle}>{modo === "trocar" ? "Troca a partir de" : "Início da vigência"}</div>
              <Input type="date" bsSize="sm" value={dataInicio} min={hojeIso()} disabled={ocupado} onChange={(e) => setDataInicio(e.target.value)} />
              <div style={ajudaStyle}>{modo === "trocar" ? "Em branco ou hoje = troca imediata. Os campos iguais passam para a versão nova." : "Em branco = ativa agora."}</div>
            </div>
          ) : null}
        </div>
        {modo === "configurar" && dados && dados.valoresDaVigenciaAnterior ? (
          <div style={{ ...avisoStyle, marginTop: "0.6rem" }}>
            Os campos vieram da última vigência (v{dados.valoresDaVigenciaAnterior.versao}, encerrada em {formatarData(dados.valoresDaVigenciaAnterior.vigenciaFim)}). Confira e ative.
          </div>
        ) : null}
        {modo === "bloqueado" ? <div style={{ ...avisoStyle, marginTop: "0.6rem" }}>Já existe uma troca agendada. Cancele antes de escolher outra versão.</div> : null}

        {loading && !dados ? (
          <div style={{ color: AUTOPILOT_COLORS.muted, fontSize: "0.82rem", padding: "1rem 0", display: "flex", gap: "0.5rem", alignItems: "center" }}>
            <Spinner size="sm" style={{ color: AUTOPILOT_COLORS.accent }} /> Carregando campos...
          </div>
        ) : (
          <>
            {ehBling ? (
              <div data-testid="bling-conta">
                <div style={secaoTituloStyle}>Conta Bling</div>
                <div style={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: "0.5rem" }}>
                  <StatusPill status={blingConectado ? "ativo" : "pendente"} labelOverride={blingConectado ? "Conectado" : "Não conectado"} />
                  <Button
                    color={blingConectado ? "secondary" : "info"}
                    size="sm"
                    outline={blingConectado}
                    onClick={conectarBling}
                    disabled={ocupado || !(vigente || agendado)}
                    title={vigente || agendado ? undefined : "Ative a integração primeiro"}
                    data-testid="bling-conectar"
                  >
                    {conectando ? <Spinner size="sm" /> : blingConectado ? "Reconectar" : "Conectar ao Bling"}
                  </Button>
                </div>
                <div style={ajudaStyle}>
                  {vigente || agendado
                    ? "Você vai até o Bling autorizar o aplicativo da Everlog e volta para esta tela. A conexão se renova sozinha; se a integração ficar 30 dias parada, reconecte."
                    : "Ative a integração primeiro; depois conecte a conta Bling."}
                </div>
              </div>
            ) : null}
            {oauth ? (
              <div data-testid="oauth-conta">
                <div style={secaoTituloStyle}>Conexão com o {oauth.nome}</div>
                <div style={{ ...ajudaStyle, marginTop: 0, marginBottom: "0.5rem" }}>{oauth.ajuda}</div>
                <div style={labelStyle}>URL de redirecionamento</div>
                <div style={{ display: "flex", gap: "0.4rem", alignItems: "center" }}>
                  <Input bsSize="sm" readOnly value={urlRetorno} data-testid="oauth-url-retorno" onFocus={(e) => e.target.select()} />
                  <Button color="secondary" size="sm" outline onClick={copiarUrlRetorno} data-testid="oauth-copiar" style={{ whiteSpace: "nowrap" }}>
                    {copiado ? "Copiado" : "Copiar"}
                  </Button>
                </div>
                <div style={ajudaStyle}>Cadastre esta URL no aplicativo que você criou no {oauth.nome}.</div>
                <div style={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: "0.5rem", marginTop: "0.75rem" }}>
                  <StatusPill status={PILL_OAUTH[estadoOAuth].status} labelOverride={PILL_OAUTH[estadoOAuth].rotulo} />
                  <Button
                    color={estadoOAuth === "conectado" ? "secondary" : "info"}
                    size="sm"
                    outline={estadoOAuth === "conectado"}
                    onClick={conectarOAuth}
                    disabled={ocupado || !(vigente || agendado) || !credencialSalva}
                    title={!(vigente || agendado) ? "Ative a integração primeiro" : !credencialSalva ? "Salve o Client ID e o Client Secret primeiro" : undefined}
                    data-testid="oauth-conectar"
                  >
                    {conectando ? <Spinner size="sm" /> : estadoOAuth === "conectado" ? "Reconectar" : `Conectar ao ${oauth.nome}`}
                  </Button>
                </div>
                <div style={ajudaStyle}>
                  {!(vigente || agendado)
                    ? `Ative a integração primeiro; depois salve a credencial do aplicativo e conecte a conta ${oauth.nome}.`
                    : !credencialSalva
                      ? "Salve o Client ID e o Client Secret primeiro; o botão libera em seguida."
                      : `Você vai até o ${oauth.nome} autorizar o aplicativo e volta para esta tela. ${oauth.renovacao}`}
                </div>
              </div>
            ) : null}
            {dados && dados.webhookUrl ? (
              <div data-testid="webhook-erp">
                <div style={secaoTituloStyle}>Endereço do webhook</div>
                <div style={{ display: "flex", gap: "0.5rem", alignItems: "center" }}>
                  <Input type="text" bsSize="sm" readOnly value={dados.webhookUrl} data-testid="webhook-erp-url" onFocus={(e) => e.target.select()} style={{ fontFamily: "monospace", fontSize: "0.8rem" }} />
                  <Button size="sm" color={webhookCopiado ? "success" : "secondary"} onClick={copiarWebhook} style={{ minWidth: 80 }}>
                    {webhookCopiado ? "Copiado!" : "Copiar"}
                  </Button>
                </div>
                <div style={ajudaStyle}>{ajudaWebhook(conector.conectorKey)}</div>
              </div>
            ) : null}
            <div style={secaoTituloStyle}>Configuração</div>
            {camposConfig.length ? (
              camposConfig.map((p) => renderCampo(p))
            ) : (
              <div style={{ color: AUTOPILOT_COLORS.muted, fontSize: "0.82rem" }}>Esta versão não tem campos de configuração cadastrados. Avise o suporte.</div>
            )}

            {camposCap.length ? (
              <>
                <div style={secaoTituloStyle}>O que esta integração troca</div>
                {camposCap.map((p) => renderCampo(p))}
              </>
            ) : null}

            {resultadoTeste ? (
              <div style={resultadoStyle(resultadoTeste.ok)} role="status">
                <strong style={{ color: resultadoTeste.ok ? AUTOPILOT_COLORS.statusSuccess : AUTOPILOT_COLORS.statusDanger }}>
                  {resultadoTeste.ok ? "Conexão OK" : "Falha na conexão"}
                </strong>
                {resultadoTeste.duracaoMs !== null ? <span style={{ color: AUTOPILOT_COLORS.muted }}> · {resultadoTeste.duracaoMs} ms</span> : null}
                <div>{resultadoTeste.mensagem}</div>
              </div>
            ) : null}

            <div style={{ display: "flex", flexWrap: "wrap", gap: "0.5rem", marginTop: "1.1rem" }}>
              <Button color="info" size="sm" outline onClick={testar} disabled={ocupado || !campos.length || !versaoSel}>
                {testando ? <Spinner size="sm" /> : "Testar conexão"}
              </Button>
              <Button color="success" size="sm" onClick={salvar} disabled={ocupado || modo === "bloqueado" || !versaoSel || (modo === "salvar" && !diff.length)}>
                {rotuloBotao()}
              </Button>
            </div>

            {vigente || agendado ? (
              <div style={{ marginTop: "1rem", paddingTop: "0.9rem", borderTop: `1px solid ${AUTOPILOT_COLORS.border}` }}>
                {confirmando === "desativar" ? (
                  <div style={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: "0.5rem" }}>
                    <span style={{ color: AUTOPILOT_COLORS.statusDanger, fontSize: "0.78rem", flex: "1 1 100%" }}>
                      O Hub para de usar {conector.nome} na hora{agendado ? " e a troca agendada é cancelada" : ""}. Os valores ficam no histórico. Desativar?
                    </span>
                    <Button color="danger" size="sm" disabled={salvando} onClick={desativar}>
                      {salvando ? "Desativando..." : "Confirmar desativação"}
                    </Button>
                    <Button color="secondary" size="sm" outline disabled={salvando} onClick={() => setConfirmando(null)}>
                      Cancelar
                    </Button>
                  </div>
                ) : (
                  <Button color="danger" size="sm" outline disabled={ocupado} onClick={() => setConfirmando("desativar")}>
                    Desativar integração
                  </Button>
                )}
              </div>
            ) : null}

            <div style={secaoTituloStyle}>Histórico de vigências</div>
            {historico.length ? (
              historico.map((h) => {
                const pill = PILL_HISTORICO[h.status] || PILL_HISTORICO.encerrado;
                return (
                  <div key={h.id} style={linhaHistoricoStyle}>
                    <div style={{ minWidth: 0 }}>
                      <div style={{ color: AUTOPILOT_COLORS.title, fontSize: "0.84rem", fontWeight: 600 }}>v{h.versao}</div>
                      <div style={{ color: AUTOPILOT_COLORS.muted, fontSize: "0.72rem" }}>
                        {h.status === "desativado"
                          ? `previsto para ${formatarData(h.vigenciaInicio)}`
                          : `${formatarData(h.vigenciaInicio)} → ${h.vigenciaFim ? formatarData(h.vigenciaFim) : "em aberto"}`}
                        {h.alteradoPor || h.criadoPor ? ` · ${h.alteradoPor || h.criadoPor}` : ""}
                      </div>
                    </div>
                    <StatusPill status={pill.status} labelOverride={pill.rotulo} />
                  </div>
                );
              })
            ) : (
              <div style={{ color: AUTOPILOT_COLORS.muted, fontSize: "0.8rem" }}>Nenhuma vigência registrada ainda.</div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
